import { createFileRoute, Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useServerFn } from "@tanstack/react-start";
import { claimFirstAdmin } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Home, BedDouble, Tags, CalendarCheck2, Users, Shield, LogOut, Menu, CalendarRange, ClipboardList, BarChart3, Settings, Sparkles, FileText, Receipt, TicketPercent, FileSpreadsheet, Link as LinkIcon, Activity } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/gestion")({
  head: () => ({ meta: [{ title: "Panel · El Pajar de Benito" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: GestionLayout,
});

const navGroups: { label: string; items: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; exact: boolean }[] }[] = [
  {
    label: "Principal",
    items: [
      { to: "/gestion", label: "Inicio", icon: Home, exact: true },
    ],
  },
  {
    label: "Operativa",
    items: [
      { to: "/gestion/operaciones", label: "Operaciones del día", icon: ClipboardList, exact: false },
      { to: "/gestion/reservas", label: "Reservas", icon: CalendarCheck2, exact: false },
      { to: "/gestion/calendario", label: "Calendario", icon: CalendarRange, exact: false },
      { to: "/gestion/limpieza", label: "Limpieza", icon: Sparkles, exact: false },
      { to: "/gestion/san-silvestre", label: "San Silvestre", icon: Users, exact: false },
    ],
  },
  {
    label: "Análisis",
    items: [
      { to: "/gestion/informes", label: "Informes y KPIs", icon: BarChart3, exact: false },
      { to: "/gestion/actividad", label: "Actividad", icon: Activity, exact: false },
    ],
  },
  {
    label: "Finanzas",
    items: [
      { to: "/gestion/facturas", label: "Facturación", icon: Receipt, exact: false },
      { to: "/gestion/iva", label: "IVA trimestral (303)", icon: FileSpreadsheet, exact: false },
      { to: "/gestion/enlaces-pago", label: "Enlaces de pago", icon: LinkIcon, exact: false },
    ],
  },
  {
    label: "Legal",
    items: [
      { to: "/gestion/viajeros", label: "SES.Hospedajes", icon: FileText, exact: false },
    ],
  },
  {
    label: "Catálogo",
    items: [
      { to: "/gestion/habitaciones", label: "Habitaciones", icon: BedDouble, exact: false },
      { to: "/gestion/tarifas", label: "Temporadas y precios", icon: Tags, exact: false },
      { to: "/gestion/promociones", label: "Códigos promocionales", icon: TicketPercent, exact: false },
    ],
  },
  {
    label: "Cuentas",
    items: [
      { to: "/gestion/clientes", label: "Clientes", icon: Users, exact: false },
      { to: "/gestion/admins", label: "Administradores", icon: Shield, exact: false },
    ],
  },
  {
    label: "Ajustes",
    items: [
      { to: "/gestion/configuracion", label: "Configuración", icon: Settings, exact: false },
    ],
  },
];

function GestionLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCount, setAdminCount] = useState<number | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const claim = useServerFn(claimFirstAdmin);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      setEmail(u.user?.email ?? null);
      if (!u.user) return;
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
      const admin = (roles ?? []).some((r) => r.role === "admin");
      setIsAdmin(admin);
      const { count } = await supabase
        .from("user_roles")
        .select("*", { count: "exact", head: true })
        .eq("role", "admin");
      setAdminCount(count ?? 0);
      setChecking(false);
    })();
  }, []);

  async function handleClaim() {
    try {
      await claim();
      toast.success("Cuenta promovida a administrador");
      window.location.reload();
    } catch (err) {
      toast.error("No se pudo asignar el rol", { description: (err as Error).message });
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Cargando panel…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/40 px-4">
        <div className="w-full max-w-md rounded-xl border bg-card p-8 text-center shadow-sm">
          <Shield className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-4 font-serif text-2xl">Acceso restringido</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Tu cuenta ({email}) no tiene permisos de administrador.
          </p>
          {adminCount === 0 ? (
            <div className="mt-6 space-y-3">
              <p className="text-sm">Todavía no existe ningún admin. Puedes asignarte el rol ahora:</p>
              <Button className="w-full" onClick={handleClaim}>Convertirme en administrador</Button>
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">
              Pide a un administrador existente que te invite desde el panel.
            </p>
          )}
          <Button variant="ghost" className="mt-4 w-full" onClick={handleSignOut}>Cerrar sesión</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-secondary/30">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r bg-card transition-transform md:static md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 shrink-0 items-center border-b px-5">
          <Link to="/gestion" className="font-serif text-lg text-primary">El Pajar · Panel</Link>
        </div>
        <nav className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
          {navGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-1.5 px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {group.label}
              </p>
              <div className="flex flex-col gap-0.5">
                {group.items.map((n) => {
                  const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
                  const Icon = n.icon;
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      onClick={() => setOpen(false)}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        active
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground/80 hover:bg-secondary",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {n.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="shrink-0 border-t p-3">
          <p className="mb-2 truncate px-2 text-xs text-muted-foreground">{email}</p>
          <Button variant="ghost" className="w-full justify-start" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col md:pl-0">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-card/80 px-4 backdrop-blur md:px-8">
          <button className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menú">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="font-serif text-lg">
            {(() => {
              for (const g of navGroups) {
                for (const n of g.items) {
                  const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
                  if (active) return n.label;
                }
              }
              return "Panel";
            })()}
          </h1>
          <Link to="/" className="text-xs text-muted-foreground hover:text-primary">Ver web pública</Link>
        </header>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      {open && (
        <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setOpen(false)} />
      )}
    </div>
  );
}